import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import {
  Plus,
  MapPin,
  Trash,
  Pencil,
  DotsThree,
  MapTrifold,
  MagnifyingGlass,
  FunnelSimple,
  House,
} from "@phosphor-icons/react";
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from "constants/index";
import { ClusterMap } from './components/ClusterMap';
import { ClusterType, UnitInCluster } from 'shared/types/cluster';
import { getIconByValue } from '@/components/dueDiligence/IconPicker';
import { Badge } from "@/components/ui/badge";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface Cluster {
  id: number;
  name: string;
  description?: string;
  type?: ClusterType;
  icon?: string;
  unitCount?: number;
  buildingCount?: number;
  units?: UnitInCluster[];
  createdAt: string;
  updatedAt: string;
}

export const Clusters: React.FC = () => {
  const navigate = useNavigate();
  const { session } = useSession();
  const [sites, setSites] = useState<Cluster[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newSiteName, setNewSiteName] = useState('');
  const [newClusterType, setNewClusterType] = useState<ClusterType>(ClusterType.FILTER);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteConfirmCluster, setDeleteConfirmCluster] = useState<Cluster | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchSites = async () => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(`${API_URL}/clusters`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }, session);

      if (!response.ok) {
        throw new Error(`Failed to fetch sites: ${response.statusText}`);
      }

      const data = await response.json();
      setSites(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching sites:', error);
      toast.error('Kon clusters niet ophalen');
      setSites([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSites();
  }, []);

  const getUnitCount = (cluster: Cluster): number => {
    return cluster.unitCount ?? cluster.units?.length ?? 0;
  };

  const getBuildingCount = (cluster: Cluster): number => {
    return cluster.buildingCount ?? getBuildings(cluster).length;
  };

  const getBuildings = (cluster: Cluster): Array<{ id: string; googlePlaceLongAddress?: string; company?: { id: number; name: string } }> => {
    if (!cluster.units) return [];
    const seen = new Set<string>();
    const result: Array<{ id: string; googlePlaceLongAddress?: string; company?: { id: number; name: string } }> = [];
    for (const unit of cluster.units) {
      if (unit.building?.id && !seen.has(unit.building.id)) {
        seen.add(unit.building.id);
        result.push({
          id: unit.building.id,
          googlePlaceLongAddress: unit.building.googlePlaceLongAddress,
          company: unit.building.company,
        });
      }
    }
    return result;
  };

  const handleCreateSite = async () => {
    if (!newSiteName.trim()) {
      toast.error('Voer een naam in voor de cluster');
      return;
    }

    try {
      setIsCreating(true);

      const endpoint = newClusterType === ClusterType.FILTER
        ? `${API_URL}/clusters/unit-filter`
        : `${API_URL}/clusters`;

      const body = newClusterType === ClusterType.FILTER
        ? {
            name: newSiteName.trim(),
            type: ClusterType.FILTER,
            unitFilterCriteria: {
              version: '1.0',
              root: {
                id: crypto.randomUUID(),
                logicalOperator: 'and',
                conditions: [],
              },
            },
          }
        : { name: newSiteName.trim() };

      const response = await fetchWithAuth(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }, session);

      if (!response.ok) {
        throw new Error(`Failed to create site: ${response.statusText}`);
      }

      const newSite = await response.json();
      setSites(prevSites => [...prevSites, newSite]);
      setIsModalOpen(false);
      setNewSiteName('');
      setNewClusterType(ClusterType.MANUAL);
      toast.success('Cluster succesvol aangemaakt');
      navigate(`/clusters/${newSite.id}`);
    } catch (error) {
      console.error('Error creating site:', error);
      toast.error('Kon cluster niet aanmaken');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteSite = async () => {
    if (!deleteConfirmCluster) return;

    try {
      setIsDeleting(true);
      const response = await fetchWithAuth(`${API_URL}/clusters/${deleteConfirmCluster.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      }, session);

      if (!response.ok) {
        throw new Error(`Failed to delete cluster: ${response.statusText}`);
      }

      toast.success('Cluster succesvol verwijderd');
      setDeleteConfirmCluster(null);
      await fetchSites();
    } catch (error) {
      console.error('Error deleting site:', error);
      toast.error('Kon cluster niet verwijderen');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newSiteName.trim()) {
      handleCreateSite();
    }
  };

  const filteredClusters = useMemo(() => {
    if (!searchQuery.trim()) return sites;
    const query = searchQuery.toLowerCase();
    return sites.filter(cluster =>
      cluster.name.toLowerCase().includes(query) ||
      (cluster.units || []).some(u =>
        u.id.toLowerCase().includes(query) ||
        u.building?.googlePlaceLongAddress?.toLowerCase().includes(query) ||
        u.boxNumber?.toLowerCase().includes(query)
      )
    );
  }, [sites, searchQuery]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner className="h-5 w-5" />
        <span className="ml-2 text-sm text-muted-foreground">Clusters laden...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Clusters</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Groepeer en beheer uw vastgoedportfolio
            </p>
          </div>
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Nieuwe cluster
          </Button>
        </div>
      </div>

      {/* Search bar - only when clusters exist */}
      {sites.length > 0 && (
        <div className="mb-6">
          <div className="relative w-full sm:w-80">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder="Zoek clusters of eenheden..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
      )}

      {/* Empty State */}
      {sites.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed rounded-lg">
          <MapTrifold size={40} className="mx-auto text-muted-foreground mb-3" weight="duotone" />
          <p className="font-medium text-sm text-foreground mb-1">
            Nog geen clusters aangemaakt
          </p>
          <p className="text-sm text-muted-foreground text-center max-w-md">
            Organiseer uw vastgoed door eenheden te groeperen in clusters.
            Perfect voor het beheren van regio's, portfolio's of projecten.
          </p>
        </div>
      ) : filteredClusters.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed rounded-lg">
          <MagnifyingGlass size={40} className="mx-auto text-muted-foreground mb-3" weight="duotone" />
          <p className="font-medium text-sm text-foreground mb-1">
            Geen clusters gevonden voor "{searchQuery}"
          </p>
          <Button
            variant="link"
            onClick={() => setSearchQuery('')}
            className="mt-1 text-sm"
          >
            Zoekopdracht wissen
          </Button>
        </div>
      ) : (
        /* Clusters Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredClusters.map((cluster) => {
            const buildings = getBuildings(cluster);
            const unitCount = getUnitCount(cluster);
            const buildingCount = getBuildingCount(cluster);
            const isFilter = cluster.type === ClusterType.FILTER;
            const ClusterIcon = cluster.icon ? getIconByValue(cluster.icon) : null;

            return (
              <Card
                key={cluster.id}
                className="group overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/clusters/${cluster.id}`)}
              >
                {/* Map Preview */}
                <div className="relative">
                  <ClusterMap
                    buildings={buildings}
                    height="130px"
                    interactive={false}
                    showPlaceholder={true}
                  />
                  {unitCount > 0 && (
                    <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded-full backdrop-blur-sm flex items-center gap-1">
                      <House className="size-3" weight="fill" />
                      {unitCount}
                    </div>
                  )}
                </div>

                <CardHeader className="pt-3 pb-2">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0 pr-2">
                      <div className="flex items-center gap-2">
                        {ClusterIcon ? (
                          <ClusterIcon className="size-4 text-brick-primary shrink-0" weight="duotone" />
                        ) : (
                          <MapTrifold className="size-4 text-muted-foreground shrink-0" weight="duotone" />
                        )}
                        <CardTitle className="text-base font-semibold truncate">
                          {cluster.name}
                        </CardTitle>
                        {isFilter && (
                          <Badge variant="secondary" className="bg-moss-secondary-light text-moss-primary text-[10px] px-1.5 py-0 h-5 shrink-0">
                            <FunnelSimple className="size-3 mr-0.5" weight="fill" />
                            Filter
                          </Badge>
                        )}
                      </div>
                      <CardDescription className="flex items-center gap-1.5 mt-1">
                        {ClusterIcon ? (
                          <ClusterIcon className={`size-3.5 shrink-0 ${isFilter ? 'text-moss-primary' : 'text-brick-primary'}`} weight="duotone" />
                        ) : (
                          <House className={`size-3.5 shrink-0 ${isFilter ? 'text-moss-primary' : 'text-muted-foreground'}`} weight="duotone" />
                        )}
                        <span>
                          {unitCount} {unitCount === 1 ? 'eenheid' : 'eenheden'}
                        </span>
                        {buildingCount > 0 && (
                          <>
                            <span className="text-border">·</span>
                            <span className="truncate">
                              {buildingCount} {buildingCount === 1 ? 'gebouw' : 'gebouwen'}
                            </span>
                          </>
                        )}
                      </CardDescription>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <DotsThree className="size-5" weight="bold" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem onClick={() => navigate(`/clusters/${cluster.id}`)}>
                          <Pencil className="size-4" />
                          <span>Bewerken</span>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setDeleteConfirmCluster(cluster)}
                          className="text-destructive focus:text-destructive focus:bg-destructive/10"
                        >
                          <Trash className="size-4" />
                          <span>Verwijderen</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>

                <CardContent className="pt-0 pb-3">
                  {isFilter ? (
                    <div className="flex items-center gap-2 py-2 px-3 rounded-md bg-moss-secondary-light/30 border border-moss-primary/15">
                      <FunnelSimple className="size-3.5 text-moss-primary shrink-0" weight="duotone" />
                      <span className="text-xs text-muted-foreground">
                        Automatisch op basis van filters
                      </span>
                    </div>
                  ) : buildings.length > 0 ? (
                    <div className="space-y-1">
                      {buildings.slice(0, 2).map((building) => (
                        <div key={building.id} className="flex items-center gap-2 text-xs text-muted-foreground">
                          <MapPin className="size-3 shrink-0" weight="fill" />
                          <span className="truncate">
                            {building.googlePlaceLongAddress || building.id}
                          </span>
                        </div>
                      ))}
                      {buildings.length > 2 && (
                        <p className="text-xs text-muted-foreground/60 pl-5">
                          +{buildings.length - 2} meer
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 py-2 px-3 rounded-md bg-muted/50 border border-dashed">
                      <House className="size-3.5 text-muted-foreground/40" weight="duotone" />
                      <span className="text-xs text-muted-foreground">
                        Nog geen eenheden
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Cluster Dialog */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[540px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapTrifold className="size-5 text-brick-primary" weight="duotone" />
              Nieuwe cluster aanmaken
            </DialogTitle>
            <DialogDescription>
              Clusters groeperen eenheden op basis van gedeelde kenmerken.
              Combineer clusters nadien tot segmenten die beslissingen op het platform aansturen.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Cluster Type Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground block">
                Type cluster
              </label>
              <div className="grid grid-cols-2 gap-3">
                {/* Filter — first, recommended */}
                <button
                  type="button"
                  onClick={() => setNewClusterType(ClusterType.FILTER)}
                  className={`relative p-4 rounded-lg border-2 text-left transition-all ${
                    newClusterType === ClusterType.FILTER
                      ? 'border-moss-primary bg-moss-primary/5'
                      : 'border-border hover:border-moss-primary/50 bg-card'
                  }`}
                >
                  <Badge variant="secondary" className="absolute top-2 right-2 bg-moss-secondary-light text-moss-primary text-[10px] px-1.5 py-0 h-5">
                    Aanbevolen
                  </Badge>
                  <div className="flex items-center gap-2 mb-1">
                    <FunnelSimple className={`size-5 ${newClusterType === ClusterType.FILTER ? 'text-moss-primary' : 'text-muted-foreground'}`} weight="duotone" />
                    <span className={`font-medium ${newClusterType === ClusterType.FILTER ? 'text-moss-primary' : 'text-foreground'}`}>
                      Filter
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Eenheden worden automatisch bijgewerkt op basis van criteria
                  </p>
                </button>

                {/* Manual — second */}
                <button
                  type="button"
                  onClick={() => setNewClusterType(ClusterType.MANUAL)}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    newClusterType === ClusterType.MANUAL
                      ? 'border-brick-primary bg-brick-primary/5'
                      : 'border-border hover:border-brick-primary/50 bg-card'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <House className={`size-5 ${newClusterType === ClusterType.MANUAL ? 'text-brick-primary' : 'text-muted-foreground'}`} weight="duotone" />
                    <span className={`font-medium ${newClusterType === ClusterType.MANUAL ? 'text-brick-primary' : 'text-foreground'}`}>
                      Handmatig
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Selecteer eenheden handmatig
                  </p>
                </button>
              </div>
            </div>

            {/* Cluster Name */}
            <div className="space-y-2">
              <label htmlFor="cluster-name" className="text-sm font-medium text-foreground block">
                Cluster naam
              </label>
              <Input
                id="cluster-name"
                placeholder="Bijv. Residentieel, Regio Antwerpen, Kustzone..."
                value={newSiteName}
                onChange={(e) => setNewSiteName(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
              />
            </div>

            {/* Context info */}
            <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg border space-y-2">
              {newClusterType === ClusterType.FILTER ? (
                <p>
                  <FunnelSimple className="size-3 inline mr-1 text-moss-primary" weight="fill" />
                  <span className="font-medium text-foreground">Altijd up-to-date</span> — filter-clusters evalueren hun criteria bij elk gebruik. Nieuwe eenheden die aan de criteria voldoen worden automatisch opgenomen.
                </p>
              ) : (
                <p>
                  <House className="size-3 inline mr-1 text-brick-primary" weight="fill" />
                  <span className="font-medium text-foreground">Handmatig beheer</span> — u kiest zelf welke eenheden tot deze cluster behoren en past dit handmatig aan.
                </p>
              )}
              <p className="pt-1 border-t border-border/50">
                <span className="font-medium text-foreground">Clusters → Segmenten</span> — combineer meerdere clusters tot segmenten.
                Bijv. cluster <em>"Residentieel"</em> + cluster <em>"Antwerpen"</em> = segment <em>"Residentieel Antwerpen"</em>.
                Segmenten sturen beslissingen aan, zoals welke leverancier verantwoordelijk is voor het onderhoud van voorzieningen.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsModalOpen(false);
                setNewSiteName('');
                setNewClusterType(ClusterType.FILTER);
              }}
            >
              Annuleren
            </Button>
            <Button
              onClick={handleCreateSite}
              disabled={!newSiteName.trim() || isCreating}
              className={newClusterType === ClusterType.FILTER
                ? "bg-moss-primary hover:bg-moss-primary/90 text-white"
                : "bg-brick-primary hover:bg-brick-primary/90 text-white"
              }
            >
              {isCreating && <Spinner className="size-4" />}
              Aanmaken
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmCluster} onOpenChange={() => setDeleteConfirmCluster(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cluster verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je de cluster "{deleteConfirmCluster?.name}" wilt verwijderen?
              {deleteConfirmCluster && getUnitCount(deleteConfirmCluster) > 0 && (
                <span className="block mt-2 p-3 rounded-lg bg-muted text-foreground">
                  De {getUnitCount(deleteConfirmCluster)} eenheden blijven behouden maar zijn niet meer gekoppeld aan een cluster.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSite}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting && <Spinner className="size-4" />}
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Clusters;
